'use client';

import { PrismicRichText } from '@prismicio/react';
import SplashButton from './SplashButton';
import HeroImage from '@/public/globe-bg.png';
import { Content } from '@prismicio/client';
import Image from 'next/image';
import FeaturedPartner from './FeaturedPartner';
import { iconMap } from '@/lib/iconMap';

interface FeaturedServicesProps {
  data: Content.FeaturedServicesDocument['data'];
  partnerData: Content.FeaturedPartnerDocument['data'];
  lang: string;
}

export default function FeaturedServices({ data, partnerData, lang }: FeaturedServicesProps) {
  return (
    <section
      id="start"
      className="relative bg-white bg-contain bg-[87%_230px] bg-no-repeat sm:bg-[center_100px]"
    >
      <Image
        src={HeroImage}
        alt="Hero background"
        fill
        sizes="100vw"
        quality={50}
        style={{
          objectFit: 'cover',
          objectPosition: 'center',
          pointerEvents: 'none',
          opacity: 0.4,
        }}
      />
      <div className="h-full w-full overflow-hidden py-12 sm:py-16">
        <div className="container">
          <div className="fade-up m-auto mb-6 grid max-w-[1200px] grid-cols-1 gap-4 sm:mb-12 md:grid-cols-[40%_60%] lg:grid-cols-2 lg:pt-4">
            {/* Left Column */}
            <div>
              <h2 className="text-3xl font-normal leading-tight text-blue-900 sm:text-3xl lg:font-light md:text-3xl lg:text-4xl">
                <PrismicRichText
                  field={data.title}
                  components={{
                    paragraph: ({ children }) => <>{children}</>,
                    preformatted: ({ children }) => <p>{children}</p>,
                    strong: ({ children }) => (
                      <strong className="bg-gradient-to-b from-yellow-300 to-yellow-600 bg-clip-text font-normal text-transparent sm:font-light">
                        {children}
                      </strong>
                    ),
                  }}
                />
              </h2>
            </div>
            {/* Right Column */}
            <div>
              <div className="py-2 text-base text-gray-500 sm:py-0 md:mx-8">
                <PrismicRichText
                  field={data.description ?? ''}
                  components={{
                    preformatted: ({ children }) => <p>{children}</p>,
                    hyperlink: ({ children, node }) => (
                      <a
                        href={node.data.url}
                        className="font-semibold text-blue-600 underline hover:text-blue-400"
                      >
                        {children}
                      </a>
                    ),
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="relative">
          {/* Scroller */}
          <div className="overflow-x-auto">
            <div className="container">
              <div className="m-auto flex w-full max-w-[1200px] gap-4">
                {data.service.map((service, index) => {
                  // Remove any existing language prefix and normalize slashes
                  const urlText = service.button_url.text ?? '';
                  const urlWithoutLang = urlText.replace(/^\/?(en-us|zh-cn|zh-hk)\//, '');
                  // Remove leading and trailing slashes, then add them back properly
                  const cleanPath = urlWithoutLang.replace(/^\/+|\/+$/g, '');
                  const buttonUrl = `/${lang}/${cleanPath}`;

                  return (
                    <Card
                      key={index}
                      title={service.title ?? ''}
                      content={service.description}
                      buttonText={service.button_text ?? ''}
                      buttonColor={service.button_color ?? 'red'}
                      buttonUrl={buttonUrl}
                      icon={service.icon ?? ''}
                    />
                  );
                })}
                <div className="w-[10px] flex-shrink-0"></div>
              </div>
            </div>
          </div>

          {/* Right fade overlay pinned to the section; pure CSS, no JS */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-white/30 to-transparent"
          />
        </div>
      </div>

      <FeaturedPartner data={partnerData} language={lang} />
    </section>
  );
}

interface CardProps {
  title: string;
  content: Content.FeaturedServicesDocumentDataServiceItem['description'];
  buttonText: string;
  buttonColor?: 'red' | 'blue' | 'yellow';
  buttonUrl: string;
  icon?: string;
}

function Card({ title, content, buttonText, buttonColor = 'red', buttonUrl, icon }: CardProps) {
  const IconComponent = icon && iconMap[icon];

  return (
    <div className="relative min-h-[380px] w-[280px] max-w-[410px] shrink-0 rounded-lg border bg-transparent p-6 shadow md:w-[300px] lg:min-h-[350px] lg:w-full">
      <div className="absolute inset-0 rounded-lg bg-gray-100 mix-blend-saturation sm:mix-blend-exclusion"></div>
      <div className="flex h-full flex-col justify-between">
        <div>
          <div className="mb-4 flex items-center">
            {IconComponent && <IconComponent className="mr-3 text-2xl text-black" />}
            <h3 className="text-lg font-semibold text-black lg:text-2xl">{title}</h3>
          </div>
          <div className="text-black/80 sm:text-base">
            <PrismicRichText
              field={content}
              components={{
                preformatted: ({ children }) => <p>{children}</p>,
                // Full-opacity black: the card's mix-blend-exclusion overlay
                // inverts it to pure white (same pipeline as the h3 title);
                // the inherited black/80 only reaches light gray.
                strong: ({ children }) => <strong className="text-black">{children}</strong>,
              }}
            />
          </div>
        </div>
        <SplashButton
          text={buttonText}
          url={buttonUrl || '#'}
          color={buttonColor}
          grow={true}
          data-value={title}
          className="ga-featured relative w-full text-center"
        />
      </div>
    </div>
  );
}
